import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import Badge from "@/components/base/Badge";
import {
  createAprDocument,
  deleteAprDocument,
  listAprDocuments,
  updateAprDocument,
  type AprDocumentItem,
  type CreateAprDocumentPayload,
} from "@/lib/api";
import SubirDocumentoModal from "@/pages/documentacion/components/SubirDocumentoModal";
import { categoriasConfig, type CategoriaDoc, type DocumentoAPR } from "@/pages/documentacion/types";

const TIPO_ICON: Record<string, string> = {
  pdf: "ri-file-pdf-line",
  xlsx: "ri-file-excel-line",
  docx: "ri-file-word-line",
  jpg: "ri-image-line",
  zip: "ri-file-zip-line",
};

const TIPO_COLOR: Record<string, string> = {
  pdf: "text-red-500 bg-red-50",
  xlsx: "text-emerald-600 bg-emerald-50",
  docx: "text-indigo-600 bg-indigo-50",
  jpg: "text-amber-600 bg-amber-50",
  zip: "text-gray-600 bg-gray-100",
};

const ESTADO_VARIANT: Record<string, "success" | "warning" | "error" | "default"> = {
  vigente: "success",
  vencido: "error",
  revision: "warning",
};

const ESTADO_LABEL: Record<string, string> = {
  vigente: "Vigente",
  vencido: "Vencido",
  revision: "En revisión",
};

export default function DocumentacionPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [docs, setDocs] = useState<DocumentoAPR[]>([]);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<CategoriaDoc | "todas">("todas");
  const [showSubir, setShowSubir] = useState(false);
  const [docSeleccionado, setDocSeleccionado] = useState<DocumentoAPR | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const categorias = Object.entries(categoriasConfig) as [CategoriaDoc, typeof categoriasConfig[CategoriaDoc]][];

  async function loadData(): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const rows = await listAprDocuments(session);
      setDocs(rows.map(mapDocumentToView));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cargar documentación APR");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken]);

  const filtered = useMemo(
    () =>
      docs.filter((d) => {
        const matchSearch =
          d.nombre.toLowerCase().includes(search.toLowerCase()) ||
          d.descripcion.toLowerCase().includes(search.toLowerCase()) ||
          d.etiquetas.some((e) => e.toLowerCase().includes(search.toLowerCase())) ||
          d.codigo.toLowerCase().includes(search.toLowerCase());
        const matchCat = filterCat === "todas" || d.categoria === filterCat;
        return matchSearch && matchCat;
      }),
    [docs, filterCat, search],
  );

  const countPorCategoria = (cat: CategoriaDoc) => docs.filter((d) => d.categoria === cat).length;
  const vencidos = docs.filter((d) => d.estado === "vencido").length;

  async function handleCreateDocument(payload: CreateAprDocumentPayload): Promise<void> {
    if (!session) return;
    await createAprDocument(session, payload);
    await loadData();
    setShowSubir(false);
  }

  async function handleEditDocument(doc: DocumentoAPR): Promise<void> {
    if (!session) return;
    const nextName = window.prompt("Nombre del documento", doc.nombre);
    if (!nextName) return;
    const nextDescription = window.prompt("Descripción del documento", doc.descripcion);
    if (nextDescription == null) return;
    const nextVersion = window.prompt("Versión", doc.version) ?? doc.version;
    const nextStatus = window.prompt("Estado (vigente|vencido|revision)", doc.estado) ?? doc.estado;
    const normalizedStatus =
      nextStatus === "vigente" || nextStatus === "vencido" || nextStatus === "revision"
        ? nextStatus
        : doc.estado;
    try {
      await updateAprDocument(session, doc.id, {
        name: nextName,
        description: nextDescription,
        version: nextVersion,
        status: normalizedStatus,
      });
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo editar documento");
    }
  }

  async function handleDeleteDocument(doc: DocumentoAPR): Promise<void> {
    if (!session) return;
    const confirmed = window.confirm(`¿Eliminar documento ${doc.nombre}?`);
    if (!confirmed) return;
    try {
      await deleteAprDocument(session, doc.id);
      if (docSeleccionado?.id === doc.id) setDocSeleccionado(null);
      await loadData();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar documento");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/administracion")}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Documentación APR</h1>
            <p className="text-sm text-gray-500 mt-0.5">Repositorio de archivos y documentos oficiales</p>
          </div>
        </div>
        <button
          onClick={() => setShowSubir(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-line" /></div>
          Subir Documento
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-50 flex-shrink-0">
            <i className="ri-folder-line text-teal-600" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-800">{docs.length}</div>
            <div className="text-xs text-gray-500">Total documentos</div>
          </div>
        </div>
        {vencidos > 0 && (
          <div className="bg-white rounded-xl border border-red-100 p-4 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
              <i className="ri-error-warning-line text-red-500" />
            </div>
            <div>
              <div className="text-xl font-bold text-red-600">{vencidos}</div>
              <div className="text-xs text-gray-500">Documentos vencidos</div>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
            <i className="ri-file-pdf-line text-amber-600" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-800">{docs.filter((d) => d.tipo === "pdf").length}</div>
            <div className="text-xs text-gray-500">Archivos PDF</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 flex-shrink-0">
            <i className="ri-file-excel-line text-emerald-600" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-800">{docs.filter((d) => d.tipo === "xlsx").length}</div>
            <div className="text-xs text-gray-500">Archivos Excel</div>
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        <div className="w-56 flex-shrink-0 space-y-1">
          <button
            onClick={() => setFilterCat("todas")}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
              filterCat === "todas" ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-folder-open-line" />
              </div>
              <span className="font-medium">Todos</span>
            </div>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${filterCat === "todas" ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
              {docs.length}
            </span>
          </button>
          {categorias.map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setFilterCat(key)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
                filterCat === key ? `${cfg.bg} ${cfg.color} font-semibold` : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={cfg.icon} />
                </div>
                <span className="font-medium text-left leading-tight">{cfg.label}</span>
              </div>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${filterCat === key ? "bg-white/60" : "bg-gray-100 text-gray-500"}`}>
                {countPorCategoria(key)}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0 space-y-4">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nombre, descripción, etiqueta o código..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
            />
          </div>

          <div className="space-y-2">
            {loading ? (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-sm text-gray-500">
                Cargando documentos...
              </div>
            ) : filtered.map((doc) => {
              const catCfg = categoriasConfig[doc.categoria];
              const tipoCls = TIPO_COLOR[doc.tipo] ?? "text-gray-500 bg-gray-100";
              const tipoIcon = TIPO_ICON[doc.tipo] ?? "ri-file-line";

              return (
                <div
                  key={doc.id}
                  onClick={() => setDocSeleccionado(docSeleccionado?.id === doc.id ? null : doc)}
                  className="bg-white rounded-xl border border-gray-100 hover:border-teal-200 transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-4 p-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${tipoCls}`}>
                      <i className={`${tipoIcon} text-lg`} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="text-sm font-semibold text-gray-800">{doc.nombre}</span>
                            <span className="text-xs font-mono text-gray-400">{doc.version}</span>
                          </div>
                          <p className="text-xs text-gray-500 line-clamp-1 mb-2">{doc.descripcion}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${catCfg.bg} ${catCfg.color}`}>
                              <i className={`${catCfg.icon} mr-1`} />{catCfg.label}
                            </span>
                            <Badge variant={ESTADO_VARIANT[doc.estado]} size="sm">{ESTADO_LABEL[doc.estado]}</Badge>
                            {doc.etiquetas.slice(0, 3).map((tag) => (
                              <span key={tag} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{tag}</span>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (doc.url) window.open(doc.url, "_blank", "noopener,noreferrer");
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-teal-100 text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
                              title="Abrir"
                            >
                              <i className="ri-eye-line text-sm" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleEditDocument(doc);
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-pencil-line text-sm" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDeleteDocument(doc);
                              }}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                              title="Eliminar"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                          <span className="text-xs text-gray-400">{doc.tamaño}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {docSeleccionado?.id === doc.id && (
                    <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/50 rounded-b-xl">
                      <div className="grid grid-cols-3 gap-4 text-xs">
                        <div>
                          <span className="text-gray-400 block mb-0.5">Subido por</span>
                          <span className="font-medium text-gray-700">{doc.subidoPor}</span>
                        </div>
                        <div>
                          <span className="text-gray-400 block mb-0.5">Fecha de subida</span>
                          <span className="font-medium text-gray-700">{doc.fechaSubida}</span>
                        </div>
                        {doc.fechaVencimiento && (
                          <div>
                            <span className="text-gray-400 block mb-0.5">Vencimiento</span>
                            <span className={`font-medium ${doc.estado === "vencido" ? "text-red-600" : "text-gray-700"}`}>{doc.fechaVencimiento}</span>
                          </div>
                        )}
                        <div className="col-span-3">
                          <span className="text-gray-400 block mb-1">Descripción completa</span>
                          <p className="text-gray-600 leading-relaxed">{doc.descripcion}</p>
                        </div>
                        <div className="col-span-3">
                          <span className="text-gray-400 block mb-1">Código</span>
                          <span className="font-mono text-gray-600">{doc.codigo}</span>
                        </div>
                        {doc.etiquetas.length > 0 && (
                          <div className="col-span-3">
                            <span className="text-gray-400 block mb-1">Etiquetas</span>
                            <div className="flex flex-wrap gap-1.5">
                              {doc.etiquetas.map((tag) => (
                                <span key={tag} className="bg-white border border-gray-200 text-gray-600 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!loading && filtered.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
                  <i className="ri-folder-open-line text-xl text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No se encontraron documentos</p>
                <p className="text-xs text-gray-400 mt-1">Intenta con otra búsqueda o categoría</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSubir && (
        <SubirDocumentoModal
          onClose={() => setShowSubir(false)}
          onConfirm={handleCreateDocument}
        />
      )}
    </div>
  );
}

function mapDocumentToView(input: AprDocumentItem): DocumentoAPR {
  return {
    id: input.id,
    codigo: input.documentCode,
    nombre: input.name,
    categoria: input.category,
    descripcion: input.description,
    fechaSubida: toDateOnly(input.uploadedAt),
    fechaVencimiento: input.expiresAt ? toDateOnly(input.expiresAt) : undefined,
    tamaño: input.sizeLabel,
    tipo: input.fileExtension,
    subidoPor: input.uploadedBy,
    version: input.version,
    estado: input.status,
    etiquetas: input.tags,
    url: input.fileUrl ?? undefined,
  };
}

function toDateOnly(value: string): string {
  if (!value) return "";
  return value.slice(0, 10);
}
